import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum AdsTxtTab {
  Records = 'records',
  Apps = 'apps',
  Domains = 'domains',
}

export enum AdsTxtAccountType {
  Direct = 'DIRECT',
  Reseller = 'RESELLER',
}

export enum AdsTxtRecordType {
  Web = 'WEB',
  Ctv = 'CTV',
}

export enum AdsTxtAppsFoundCriteria {
  AllApps = 'ALL_APPS',
  ZeroRecordsFound = 'ZERO_RECORDS_FOUND',
  IncompleteList = 'INCOMPLETE_LIST',
  CompleteList = 'COMPLETE_LIST',
  MissingCrawl = 'MISSING_CRAWL',
}

export enum AdsTxtDomainStatus {
  Valid = 'VALID',
  Incomplete = 'INCOMPLETE',
}

export enum AdsTxtEntityStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum AdsTxtResultState {
  Complete = 'COMPLETE',
  Incomplete = 'INCOMPLETE',
  NotAvailable = 'NOT_AVAILABLE',
}

export const adsTxtPageSizes = [5, 10, 25, 50, 100] as const;

const idSchema = z.union([z.string(), z.number()]).transform(String);
const optionalString = nullableOptional(z.string());
const nullableNumber = z.number().nullish();

const referenceSchema = z.looseObject({
  id: idSchema,
  name: optionalString,
  displayName: optionalString,
});

export const adsTxtRecordSchema = z.looseObject({
  id: idSchema,
  domain: z.string(),
  publisherAccountId: z.string(),
  accountType: z.enum(AdsTxtAccountType),
  certAuthority: optionalString,
  comment: optionalString,
  note: optionalString,
  condensed: z.boolean().nullish(),
  demandPartnerId: nullableOptional(idSchema),
  demandPartner: referenceSchema.nullish(),
  demandPartnerName: optionalString,
  priority: nullableNumber,
  web: z.boolean().nullish(),
  ctv: z.boolean().nullish(),
  network: referenceSchema.nullish(),
});

export const adsTxtRecordsPageSchema = z.looseObject({
  content: z.array(adsTxtRecordSchema),
  totalElements: z.number(),
});

export const adsTxtAppSchema = z.looseObject({
  id: idSchema,
  appStoreId: optionalString,
  appStoreUrl: optionalString,
  adsTxtLocation: optionalString,
  publisherNames: z
    .union([z.string(), z.array(z.string())])
    .nullish()
    .transform((value) => (Array.isArray(value) ? value.join(', ') : (value ?? ''))),
  platform: referenceSchema.nullish(),
  adstxtFound: nullableNumber,
  adstxtTotal: nullableNumber,
  adstxtCrawledOn: optionalString,
});

export const adsTxtAppsPageSchema = z.looseObject({
  content: z.array(adsTxtAppSchema),
  totalElements: z.number(),
});

export const adsTxtDomainSchema = z
  .looseObject({
    id: z.looseObject({ domainId: idSchema, publisherId: idSchema }),
    domain: z.looseObject({
      url: optionalString,
      status: z.enum(AdsTxtEntityStatus).nullish(),
    }),
    publisher: referenceSchema.nullish(),
    adstxtFound: nullableNumber,
    adstxtTotal: nullableNumber,
    adstxtCrawledOn: optionalString,
  })
  .transform((value) => ({
    domainId: value.id.domainId,
    publisherId: value.id.publisherId,
    url: value.domain.url ?? '',
    status: value.domain.status ?? AdsTxtEntityStatus.Active,
    publisherName: value.publisher?.name ?? value.publisher?.displayName ?? '',
    adstxtFound: value.adstxtFound,
    adstxtTotal: value.adstxtTotal,
    adstxtCrawledOn: value.adstxtCrawledOn,
  }));

export const adsTxtDomainsPageSchema = z.looseObject({
  content: z.array(adsTxtDomainSchema),
  totalElements: z.number(),
});

export const adsTxtDomainCountsSchema = z.record(z.string(), z.number());
export const adsTxtReferencePageSchema = z.looseObject({
  content: z.array(referenceSchema),
  totalElements: z.number(),
});

export const adsTxtMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.union([z.null(), z.looseObject({}), z.array(z.looseObject({}))]),
);

export interface AdsTxtRecordsFilter {
  searchTerm: string;
  types: AdsTxtRecordType[];
  page: number;
  size: number;
}

export interface AdsTxtAppsFilter {
  searchTerm: string;
  publisherIds: string[];
  foundCriteria: AdsTxtAppsFoundCriteria;
  page: number;
  size: number;
}

export interface AdsTxtDomainsFilter {
  searchTerm: string;
  statuses: AdsTxtDomainStatus[];
  page: number;
  size: number;
}

export interface AdsTxtRecordPayload {
  domain: string;
  publisherAccountId: string;
  accountType: AdsTxtAccountType;
  demandPartnerId: string;
  certAuthority: string | null;
  priority: number | null;
  comment: string | null;
  note: string | null;
  web: boolean;
  ctv: boolean;
}

export type AdsTxtRecord = z.infer<typeof adsTxtRecordSchema>;
export type AdsTxtApp = z.infer<typeof adsTxtAppSchema>;
export type AdsTxtDomain = z.infer<typeof adsTxtDomainSchema>;
export type AdsTxtReference = z.infer<typeof referenceSchema>;

export function adsTxtRecordLine(record: AdsTxtRecord) {
  return [record.domain, record.publisherAccountId, record.accountType, record.certAuthority]
    .filter((value) => value !== '')
    .join(', ');
}

export function parseAdsTxtRecordLine(value: string): Partial<AdsTxtRecordPayload> {
  const parts = value.split(',').map((part) => part.trim());
  const accountType = parts[2]?.toUpperCase();
  return {
    ...(parts[0] ? { domain: parts[0].toLowerCase() } : {}),
    ...(parts[1] ? { publisherAccountId: parts[1] } : {}),
    ...(accountType === AdsTxtAccountType.Direct || accountType === AdsTxtAccountType.Reseller
      ? { accountType }
      : {}),
    ...(parts[3] ? { certAuthority: parts[3] } : {}),
  };
}

export function resolveAdsTxtResultState(found: number | null, total: number | null) {
  if (total === null) return AdsTxtResultState.NotAvailable;
  return found === total ? AdsTxtResultState.Complete : AdsTxtResultState.Incomplete;
}

export function formatAdsTxtDomainResult(found: number | null, total: number | null) {
  return total === null ? 'N/A' : `${String(found ?? 0)}/${String(total)}`;
}

export function hasAdsTxtCrawlDateChanged(
  previousDate: string | null | undefined,
  currentDate: string | null | undefined,
) {
  if (!currentDate) return false;
  return currentDate.slice(0, 19) !== previousDate?.slice(0, 19);
}
